/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { Grid } from '@radix-ui/themes'
import { useParams } from '@tanstack/react-router'
import { useGetClassDetail } from '@/services/classDetailService'
import { useModulesStore } from '@/stores/useModulesStore'
import SummaryStats from '@/components/ui/SummaryStats/summary-stats'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import BreadScrum from '@/components/BreadScrum'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import MarkIcon from '@/components/svg/MarkIcon'
import ChapterTable from './components/ClassDetail/ChapterTable'
import MockExamTable from './components/ClassDetail/MockExamTable'
import PracticeTestTable from './components/ClassDetail/PracticeTestTable'
import StudentTable from './components/ClassDetail/StudentTable'
import { ClassDetailTabs } from './constants'

const ClassDetails = () => {
  const { classId, moduleId } = useParams({
    from: '/_authenticated/class/$classId/$moduleId',
  })
  const { selectedModule } = useModulesStore()
  const { classDetail, isLoadingClassDetail } = useGetClassDetail(classId)
  const { setSelectedModule } = useModulesStore()
  const [tab, setTab] = useState(ClassDetailTabs.question)

  const statData = useMemo(() => {
    return [
      {
        title: 'Practice Test Attempts',
        value: String(classDetail?.pt_attempts ?? 'N/A'),
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Average Practice Test Score',
        value: String(classDetail?.marked_pt ?? 'N/A'),
        icon: <MarkIcon size={48} />,
        showTooltip: true,
        valueSuffix: classDetail?.marked_pt === null ? '' : '%',
      },
      {
        title: 'Mock Exam Attempts',
        value: String(classDetail?.mock_attempts ?? 'N/A'),
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Average Mock Exam Score',
        value: String(classDetail?.marked_mock ?? 'N/A'),
        icon: <MarkIcon size={48} />,
        showTooltip: true,
        valueSuffix: classDetail?.marked_mock === null ? '' : '%',
      },
    ]
  }, [classDetail])

  useEffect(() => {
    if (moduleId) {
      setSelectedModule({
        name: classDetail?.module_name ?? '',
        description: '',
        total_classes: 0,
        total_practice_test: 0,
        total_mock_exam: 0,
        id: Number(moduleId),
      })
    }
  }, [moduleId, classDetail])

  const tutorsArr = Array.isArray(classDetail?.tutor)
      ? (classDetail?.tutor as string[])
      : []
    const label = tutorsArr.length > 1 ? 'Tutors:' : 'Tutor:'

  return (
    <Main>
      <BreadScrum
        breadcrumbs={[
          { link: '/class/', label: 'Class management' },
          {
            link: '',
            label: classDetail?.module_name ?? selectedModule?.name ?? '',
          },
          { link: '', label: tab ?? '' },
        ]}
      />
      <div className='mb-4'>
        <div className='flex items-center gap-4'>
          <PageTitle title={classDetail?.name ?? ''} />
        </div>
        {tutorsArr.length === 0 ? (
          <p>
            <span className='font-light'>{label}</span> -
          </p>
        ) : (
          <div className='grid grid-cols-[auto_1fr] gap-x-2'>
            <span className='font-light whitespace-nowrap'>{label}</span>
            <ul className='list-none pl-0 space-y-1'>
              {tutorsArr.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className='flex w-full flex-col items-start gap-4 pt-4'>
        {isLoadingClassDetail ? (
          <Grid columns={'4'} gap={'12px'} className='h-[160px] w-full'>
            {[...Array(4)].map(() => (
              <Skeleton className='h-40 w-full bg-[#d6d6d6]' />
            ))}
          </Grid>
        ) : (
          <SummaryStats
            data={statData}
            columns={'4'}
            gap={'12px'}
            width={'100%'}
          />
        )}
        <Tabs
          defaultValue={ClassDetailTabs.question}
          className='mt-2 w-full flex-col gap-4'
        >
          <TabsList className='gap-3 bg-transparent'>
            {Object.values(ClassDetailTabs).map((item: string) => (
              <TabsTrigger
                value={item}
                key={item}
                className='p-1 text-base text-[#667085]'
                onClick={() => setTab(item)}
              >
                {item}
              </TabsTrigger>
            ))}
          </TabsList>
          {!isLoadingClassDetail && (
            <>
              <TabsContent value={ClassDetailTabs.question}>
                <ChapterTable classId={classDetail?.id.toString() ?? ''} />
              </TabsContent>
              <TabsContent value={ClassDetailTabs.student}>
                <StudentTable />
              </TabsContent>
              <TabsContent value={ClassDetailTabs.mock_exam}>
                <MockExamTable classId={classId} moduleId={moduleId} />
              </TabsContent>
              <TabsContent value={ClassDetailTabs.practice_test}>
                <PracticeTestTable classId={classId} moduleId={moduleId} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </Main>
  )
}

export default ClassDetails
